import { useEffect, useRef } from 'react'
import type { Driver } from './driver.js'

/**
 * One stop of the tour: something on screen, and a note pinned beside it.
 *
 * The tour is not a list of steps handed to the library up front. Its stops
 * cross screens that React swaps out - a ballot becomes a cinematic becomes a
 * board - so the page decides which stop is current, and this only puts the
 * spotlight and the note where that stop says.
 */
export interface Stop {
  /** A selector for what to light up. Without one, the note sits mid-screen. */
  element?: string
  title: string
  body: string
  side?: 'top' | 'bottom' | 'left' | 'right'
  /**
   * The label for Next, or null when the stop waits for a move instead: the
   * lit element stays live and the tour moves on when the move is made.
   */
  next: string | null
  /** Leaves the lit element usable on a stop that has Next as well. */
  live?: boolean
}

/** Marks the element a stop is waiting on, so it stays clickable - see below. */
const LIVE = 'data-tour-live'

export interface TourControls {
  onNext: () => void
  onBack?: () => void
  onSkip: () => void
}

/**
 * Shows `stop` - or nothing, while a cinematic has the screen. The library is
 * loaded on first use, so a page that never starts the tour never fetches it.
 */
export function useTourStop(stop: Stop | null, controls: TourControls, variant = 'memo') {
  const driverRef = useRef<Driver | null>(null)
  // Read through a ref, so a new callback each render does not redraw the note.
  const latest = useRef(controls)
  latest.current = controls

  useEffect(() => {
    let cancelled = false
    if (!stop) {
      driverRef.current?.destroy()
      return
    }
    void (async () => {
      const { driver } = await import('./driver.js')
      if (cancelled) return
      driverRef.current?.destroy()
      const live = stop.next === null || stop.live === true
      const buttons = [
        ...(stop.next ? (['next'] as const) : []),
        ...(latest.current.onBack ? (['previous'] as const) : []),
        'close' as const,
      ]
      const d = driver({
        animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        allowClose: true,
        // A stray tap on the dark is not a request to leave the tour.
        overlayClickBehavior: () => {},
        allowKeyboardControl: false,
        overlayOpacity: 0.62,
        stagePadding: 6,
        stageRadius: 4,
        popoverClass: variant,
        popoverOffset: 12,
        disableActiveInteraction: !live,
        smoothScroll: true,
        onPopoverRender: (popover) => {
          popover.closeButton.textContent = 'Skip tour'
          popover.closeButton.setAttribute('aria-label', 'Skip the tour')
          popover.wrapper.setAttribute('role', 'dialog')
          popover.wrapper.setAttribute('aria-label', stop.title)
          // Where Next would be, on a stop that waits for a move instead.
          if (!stop.next) {
            const waiting = document.createElement('p')
            waiting.className = 'memo__waiting'
            waiting.textContent = 'Waiting for your move'
            popover.description.after(waiting)
          }
          // The note is what to read next, so it is where focus goes.
          ;(stop.next ? popover.nextButton : popover.wrapper).focus({ preventScroll: true })
        },
        onNextClick: () => latest.current.onNext(),
        onPrevClick: () => latest.current.onBack?.(),
        onCloseClick: () => latest.current.onSkip(),
        onDestroyed: (element) => element?.removeAttribute(LIVE),
      })
      d.highlight({
        ...(stop.element ? { element: stop.element } : {}),
        popover: {
          title: stop.title,
          description: stop.body,
          side: stop.side ?? 'bottom',
          align: 'center',
          showButtons: buttons,
          nextBtnText: stop.next ?? '',
          prevBtnText: 'Back',
        },
      })
      // The library marks the lit element with a class, and nothing else lets
      // a pointer through the overlay. React owns that element's className: a
      // button that re-labels itself on the first of two clicks (the desktop
      // confirm) rewrites it, the mark is gone, and the second click lands on
      // nothing. An attribute React never set survives its renders.
      if (live) d.getActiveElement()?.setAttribute(LIVE, '')
      driverRef.current = d
    })()
    return () => {
      cancelled = true
    }
  }, [stop, variant])

  // Escape leaves the tour, as the button does.
  useEffect(() => {
    if (!stop) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') latest.current.onSkip()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [stop])

  useEffect(() => () => driverRef.current?.destroy(), [])

  return {
    /** Re-measure the lit element, after the page under it has moved. */
    refresh: () => driverRef.current?.refresh(),
  }
}
